import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@user/store/hooks'
import { useD2DTerritoriesEnabled } from '@user/store/tenantFeatures/hooks/useD2DTerritoriesEnabled'
import { fetchTerritorySummaries } from '../api'
import { selectTerritorySummariesStatus } from '../slice'

/**
 * Every territory's name and colour, once (WWM-17508).
 *
 * For screens away from the map that still have to NAME a territory -- the lead assignment rules
 * page, where a territory can be a condition. Follows the useGetXOnMount convention that page
 * already uses for branches, campaigns and the rest, rather than borrowing useTerritoryLayer,
 * whose job is the viewport-driven geometry feed.
 *
 * Geometry-less and cached by the slice, so a user who has already been to the map pays nothing.
 */
export function useGetTerritoriesOnMount(): void {
  const dispatch = useAppDispatch()
  const enabled = useD2DTerritoriesEnabled()
  const status = useSelector(selectTerritorySummariesStatus)

  // Retried once per mount after a failure, rather than never.
  //
  // Gating on 'idle' alone made a rejection permanent for the session: summariesStatus sticks at
  // 'error' in the store, so coming back to the rules page never asked again and every
  // territory-typed condition was left unable to name its territory until a full reload.
  //
  // Once per mount, not on every 'error', for the reason useTerritoryLayer gives where it solves
  // the same problem: re-dispatching straight off the error state spins, because the rejection
  // re-enters this effect immediately. That hook rate-limits by viewport; this one has no
  // viewport, so a mount is the natural unit -- the user navigating back is the retry.
  const retriedAfterErrorRef = useRef(false)

  useEffect(() => {
    if (!enabled) return
    if (status === 'loading' || status === 'loaded') return

    if (status === 'error') {
      if (retriedAfterErrorRef.current) return
      retriedAfterErrorRef.current = true
    }

    dispatch(fetchTerritorySummaries())
  }, [dispatch, enabled, status])
}
